import { z } from 'zod';
import { uuidSchema } from './common.schema.js';

// ─── Person ↔ Patient link / unlink ──────────────────────────────────────────
export const LinkPersonToPatientRequestSchema = z.object({
  hospital_id: uuidSchema,
  person_id: uuidSchema.describe('UUID of the person (e.g. mother) to link'),
  patient_id: uuidSchema.describe('UUID of the patient (e.g. infant) to link to'),
  relationship_type: z.string().max(50).default('BIOLOGICAL').describe('Relationship type (defaults to BIOLOGICAL)'),
  link_as_mother: z.boolean().optional().default(false).describe(
    'When true and the person is a DONOR, links the baby as own baby (mother-infant). ' +
    'This auto-promotes the donor to BOTH (shown in both mother and donor lists). ' +
    'Ignored when person_type is MOTHER or BOTH.',
  ),
});
export type LinkPersonToPatientRequest = z.infer<typeof LinkPersonToPatientRequestSchema>;

export const UnlinkPersonFromPatientRequestSchema = z.object({
  link_id: uuidSchema.describe('UUID of the mother_infant_links row to deactivate'),
});
export type UnlinkPersonFromPatientRequest = z.infer<typeof UnlinkPersonFromPatientRequestSchema>;

export const LinkPersonToPatientResponseSchema = z.object({
  link_id: uuidSchema,
  hospital_id: uuidSchema,
  person_id: uuidSchema,
  patient_id: uuidSchema,
  relationship_type: z.string(),
  effective_from: z.coerce.date(),
  effective_to: z.coerce.date().nullable(),
});
export type LinkPersonToPatientResponse = z.infer<typeof LinkPersonToPatientResponseSchema>;

// ─── Bulk link ────────────────────────────────────────────────────────────────

export const BulkLinkPersonToPatientRequestSchema = z.object({
  hospital_id: uuidSchema,
  person_id: uuidSchema.describe('UUID of the person (e.g. mother) to link'),
  link_as_mother: z.boolean().optional().default(false).describe(
    'When true and the person is a DONOR, links babies as own babies (mother-infant). ' +
    'This auto-promotes the donor to BOTH (shown in both mother and donor lists). ' +
    'Ignored when person_type is MOTHER or BOTH.',
  ),
  links: z.array(
    z.object({
      patient_id: uuidSchema.describe('UUID of the patient (e.g. infant) to link to'),
      relationship_type: z.string().max(50).default('BIOLOGICAL').describe('Relationship type (defaults to BIOLOGICAL)'),
    }),
  ).min(1, 'At least one link is required'),
});
export type BulkLinkPersonToPatientRequest = z.infer<typeof BulkLinkPersonToPatientRequestSchema>;

export const BulkLinkPersonToPatientResponseSchema = z.object({
  created: z.array(LinkPersonToPatientResponseSchema),
  total: z.number(),
});
export type BulkLinkPersonToPatientResponse = z.infer<typeof BulkLinkPersonToPatientResponseSchema>;

// ─── GET mother_infant_links ──────────────────────────────────────────────────

/**
 * Query params: exactly one of patient_id or person_id must be provided.
 */
export const GetMotherInfantLinksQuerySchema = z
  .object({
    hospital_id: uuidSchema,
    patient_id:  uuidSchema.optional().describe('Return all persons linked to this patient'),
    person_id:   uuidSchema.optional().describe('Return all patients linked to this person'),
  })
  .refine((d) => !!(d.patient_id ?? d.person_id), {
    message: 'Provide exactly one of patient_id or person_id',
  })
  .refine((d) => !(d.patient_id && d.person_id), {
    message: 'Provide exactly one of patient_id or person_id, not both',
  });
export type GetMotherInfantLinksQuery = z.infer<typeof GetMotherInfantLinksQuerySchema>;

/** Shape returned for each milk_providers row when querying by patient_id */
export const LinkedMotherSummarySchema = z.object({
  person_id:     uuidSchema,
  hospital_id:   uuidSchema,
  person_type:   z.string(),
  mrn:           z.string().nullable(),
  donor_code:    z.string().nullable(),
  first_name:    z.string(),
  last_name:     z.string(),
  date_of_birth: z.coerce.date().nullable(),
  blood_type:    z.string().nullable(),
  phone:         z.string().nullable(),
  email:         z.string().nullable(),
  is_active:     z.boolean(),
  // link metadata
  link_id:           uuidSchema,
  relationship_type: z.string(),
  effective_from:    z.coerce.date(),
  effective_to:      z.coerce.date().nullable(),
});
export type LinkedMotherSummary = z.infer<typeof LinkedMotherSummarySchema>;

/** Shape returned for each patients row when querying by person_id */
export const LinkedPatientSummarySchema = z.object({
  patient_id:     uuidSchema,
  hospital_id:    uuidSchema,
  mrn:            z.string(),
  first_name:     z.string(),
  middle_name:    z.string().optional(),
  last_name:      z.string(),
  date_of_birth:  z.coerce.date(),
  gender:         z.string().nullable(),
  birth_weight_grams: z.number().nullable(),
  is_active:      z.boolean(),
  // location
  facility_name:  z.string().nullable().optional(),
  unit_name:      z.string().nullable().optional(),
  bed_number:     z.string().nullable().optional(),
  // allergies
  allergies: z.array(z.object({
    id:            uuidSchema,
    allergen_id:   uuidSchema,
    allergen_name: z.string(),
    severity_id:   uuidSchema.nullable().optional(),
    severity_name: z.string().nullable().optional(),
  })).optional(),
  // link metadata
  link_id:           uuidSchema,
  relationship_type: z.string(),
  effective_from:    z.coerce.date(),
  effective_to:      z.coerce.date().nullable(),
});
export type LinkedPatientSummary = z.infer<typeof LinkedPatientSummarySchema>;

/** Discriminated response depending on which query param was passed */
export const GetMotherInfantLinksResponseSchema = z.discriminatedUnion('query_type', [
  z.object({
    query_type: z.literal('by_patient'),
    patient_id: uuidSchema,
    persons:    z.array(LinkedMotherSummarySchema),
  }),
  z.object({
    query_type: z.literal('by_person'),
    person_id:  uuidSchema,
    patients:   z.array(LinkedPatientSummarySchema),
  }),
]);
export type GetMotherInfantLinksResponse = z.infer<typeof GetMotherInfantLinksResponseSchema>;
